import { useState } from "react";
import { AppLayout } from "../components/Layout/AppLayout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import api from "../lib/api";
import { toast } from "sonner";
import { UploadCloud, Terminal } from "lucide-react";

const TARGETS = ["codigo", "tipo", "area", "elemento", "detalle", "fecha_alta", "fecha_cierre", "distrito", "barrio", "calle", "numero", "lon", "lat", "canal_entrada"];

export default function Import() {
  const [file, setFile] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanLines, setScanLines] = useState([]);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [keepFreeText, setKeepFreeText] = useState(false);
  const [result, setResult] = useState(null);

  const runScanEffect = () =>
    new Promise((resolve) => {
      const lines = ["> analizando cabeceras...", "> detectando codificación...", "> sugiriendo mapeo de columnas...", "> listo."];
      setScanning(true);
      setScanLines([]);
      let i = 0;
      const interval = setInterval(() => {
        setScanLines((prev) => [...prev, lines[i]]);
        i++;
        if (i >= lines.length) {
          clearInterval(interval);
          setTimeout(() => {
            setScanning(false);
            resolve();
          }, 300);
        }
      }, 220);
    });

  const onFile = async (f) => {
    setFile(f);
    setResult(null);
    const fd = new FormData();
    fd.append("file", f);
    await runScanEffect();
    const { data } = await api.post("/iris/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setPreview(data);
    setMapping(data.suggested_mapping || {});
  };

  const confirmar = async () => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("mapping_json", JSON.stringify(mapping));
    fd.append("keep_free_text", keepFreeText);
    const { data } = await api.post("/iris/import/confirm", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setResult(data);
    toast.success(`Importación completada: ${data.creadas} incidencias creadas`);
  };

  return (
    <AppLayout title="Importar fichas IRIS derivadas (Conector B)">
      {!preview && !scanning && (
        <label
          className="flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed border-border h-64 cursor-pointer hover:bg-secondary/40 transition-colors"
          data-testid="import-dropzone"
        >
          <UploadCloud className="h-10 w-10 text-valoriza-leaf" strokeWidth={1.5} />
          <p className="text-sm font-bold">Arrastra o selecciona el CSV / Excel de fichas derivadas</p>
          <p className="text-xs text-muted-foreground">Deduplicación automática por código de ficha IRIS</p>
          <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={(e) => e.target.files[0] && onFile(e.target.files[0])} />
        </label>
      )}

      {scanning && (
        <Card className="bg-noc-bg border-valoriza-leaf/30">
          <CardContent className="p-8 font-mono text-sm text-valoriza-leaf space-y-1" data-testid="import-scan-terminal">
            <p className="flex items-center gap-2 mb-3 text-white/70">
              <Terminal className="h-4 w-4" /> sentinel-bcn // conector-b
            </p>
            {scanLines.map((l, i) => (
              <p key={i}>{l}</p>
            ))}
          </CardContent>
        </Card>
      )}

      {preview && !result && (
        <div className="space-y-6" data-testid="import-mapping-wizard">
          {preview.personal_data_columns?.length > 0 && (
            <Card className="border-accent-warning/40 bg-accent-warning/5">
              <CardContent className="p-4 text-sm">
                Se han detectado columnas con posibles datos personales de ciudadanos: <b>{preview.personal_data_columns.join(", ")}</b>. Se descartan por defecto (RGPD §7).
              </CardContent>
            </Card>
          )}
          <Card>
            <CardContent className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              {TARGETS.map((t) => (
                <div key={t} className="space-y-1">
                  <Label className="text-xs uppercase tracking-widest">{t}</Label>
                  <Select value={mapping[t] || "__none__"} onValueChange={(v) => setMapping((m) => ({ ...m, [t]: v === "__none__" ? undefined : v }))}>
                    <SelectTrigger data-testid={`mapping-select-${t}`}>
                      <SelectValue placeholder="Sin mapear" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Sin mapear</SelectItem>
                      {preview.columns.map((c) => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
              <div className="flex items-center gap-2 md:col-span-2 pt-2">
                <Switch checked={keepFreeText} onCheckedChange={setKeepFreeText} data-testid="keep-free-text-switch" />
                <Label className="text-xs">Conservar texto libre (puede contener datos personales — requiere justificación de Admin)</Label>
              </div>
            </CardContent>
          </Card>
          <Button data-testid="confirm-import-button" onClick={confirmar} className="bg-valoriza-leaf text-black font-bold hover:bg-valoriza-leaf/90">
            Confirmar importación ({preview.total_rows} filas)
          </Button>
        </div>
      )}

      {result && (
        <Card className="border-valoriza-leaf/40" data-testid="import-result-summary">
          <CardContent className="p-8 grid grid-cols-4 gap-6 text-center">
            <div>
              <p className="text-4xl font-black text-valoriza-leaf">{result.creadas}</p>
              <p className="text-xs uppercase tracking-widest text-muted-foreground mt-1">Creadas</p>
            </div>
            <div>
              <p className="text-4xl font-black">{result.duplicadas}</p>
              <p className="text-xs uppercase tracking-widest text-muted-foreground mt-1">Duplicadas</p>
            </div>
            <div>
              <p className="text-4xl font-black text-accent-warning">{result.descartadas_tematica || 0}</p>
              <p className="text-xs uppercase tracking-widest text-muted-foreground mt-1">Fuera de alcance</p>
            </div>
            <div>
              <p className="text-4xl font-black">{result.pct_geocodificadas}%</p>
              <p className="text-xs uppercase tracking-widest text-muted-foreground mt-1">Geocodificadas</p>
            </div>
          </CardContent>
        </Card>
      )}
    </AppLayout>
  );
}
